import { useMemo, useState } from "react";
import { Accordion, Badge, Button, Form, Modal } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useGetCategoryDimensions } from "../../../hooks/useGetCategoryDimensions";
import { useGetCategoryDimensionOptions, CategoryDimensionOptionRow } from "../../../hooks/useGetCategoryDimensionOptions";
import Loader from "../../../components/Loader";
import QuestionFormModal, { QuestionFormData } from "./QuestionFormModal";
import DimensionOptionFormModal, { DimensionOptionFormData } from "./DimensionOptionFormModal";
import { EditSubCategory } from "../../../types";
import { useCreateCategoryDimension } from "../../../hooks/useCreateCategoryDimension";
import { useDeleteCategoryDimension } from "../../../hooks/useDeleteCategoryDimension";
import { useUpdateCategoryDimension } from "../../../hooks/useUpdateCategoryDimension";
import { useCreateDimensionOption } from "../../../hooks/useCreateDimensionOption";
import { useUpdateDimensionOption } from "../../../hooks/useUpdateDimensionOption";
import { useDeleteDimensionOption } from "../../../hooks/useDeleteDimensionOption";

const LANGUAGES = [
	{ key: "en-us", label: "EN" },
	{ key: "de-ch", label: "DE-CH" },
	{ key: "de-de", label: "DE-DE" },
	{ key: "fr-fr", label: "FR" },
	{ key: "it-it", label: "IT" },
] as const;

type LanguageKey = (typeof LANGUAGES)[number]["key"];

interface GroupedOption {
	optionId: number;
	optionUid: string;
	sequence: number;
	isDeleted: number;
	isTextbox: boolean;
	translations: Partial<Record<LanguageKey, string>>;
}

function groupOptionsByRow(options: CategoryDimensionOptionRow[]): GroupedOption[] {
	const map = new Map<number, GroupedOption>();

	options.forEach((opt) => {
		if (!map.has(opt.CategoryDimensionOption_ID)) {
			map.set(opt.CategoryDimensionOption_ID, {
				optionId: opt.CategoryDimensionOption_ID,
				optionUid: opt.CategoryDimensionOption_UID,
				sequence: opt.Sequence,
				isDeleted: opt.IsDeleted,
				isTextbox: !!opt.IsTextbox,
				translations: {},
			});
		}
		const entry = map.get(opt.CategoryDimensionOption_ID)!;
		const locale = opt.Locale?.toLowerCase() as LanguageKey;
		if (locale) {
			entry.translations[locale] = opt.LocalizedValue;
		}
	});

	return Array.from(map.values()).sort((a, b) => a.sequence - b.sequence);
}

function DimensionOptions({ categoryDimensionUid, portalId, isExpanded }: { categoryDimensionUid: string; portalId: number; isExpanded: boolean }) {
	const { data: options, isLoading, refetch } = useGetCategoryDimensionOptions({
		portalId,
		categoryDimensionUid,
		enabled: isExpanded,
	});

	const createDimensionOption = useCreateDimensionOption();
	const updateDimensionOption = useUpdateDimensionOption();
	const deleteDimensionOption = useDeleteDimensionOption();

	const [showOptionModal, setShowOptionModal] = useState(false);
	const [optionModalTitle, setOptionModalTitle] = useState("New Option");
	const [editingOptionUid, setEditingOptionUid] = useState<string | null>(null);
	const [optionModalDefaults, setOptionModalDefaults] = useState<Partial<DimensionOptionFormData> | undefined>(undefined);
	const [showDeleteOptionConfirm, setShowDeleteOptionConfirm] = useState(false);
	const [deleteOptionTarget, setDeleteOptionTarget] = useState<{ uid: string; name: string } | null>(null);

	const grouped = useMemo(() => {
		if (!options) return [];
		return groupOptionsByRow(options);
	}, [options]);

	const handleAddOption = () => {
		setEditingOptionUid(null);
		setOptionModalTitle("New Option");
		setOptionModalDefaults(undefined);
		setShowOptionModal(true);
	};

	const handleEditOption = (opt: GroupedOption) => {
		setEditingOptionUid(opt.optionUid);
		setOptionModalTitle("Edit Option");
		setOptionModalDefaults({
			"en-us": opt.translations["en-us"] || "",
			"de-ch": opt.translations["de-ch"] || "",
			"de-de": opt.translations["de-de"] || "",
			"fr-fr": opt.translations["fr-fr"] || "",
			"it-it": opt.translations["it-it"] || "",
			sequence: opt.sequence,
			isTextbox: opt.isTextbox,
		});
		setShowOptionModal(true);
	};

	const handleOptionSubmit = (data: DimensionOptionFormData) => {
		const localizations = {
			"en-us": data["en-us"],
			"de-ch": data["de-ch"],
			"de-de": data["de-de"],
			"fr-fr": data["fr-fr"],
			"it-it": data["it-it"],
		};

		if (editingOptionUid) {
			updateDimensionOption.mutate(
				{
					categoryDimensionOptionUid: editingOptionUid,
					sequence: data.sequence,
					isTextbox: data.isTextbox,
					localizations,
				},
				{ onSuccess: () => refetch() },
			);
		} else {
			createDimensionOption.mutate(
				{
					categoryDimensionUid,
					sequence: data.sequence,
					isTextbox: data.isTextbox,
					localizations,
				},
				{ onSuccess: () => refetch() },
			);
		}
	};

	const handleDeleteOptionClick = (opt: GroupedOption) => {
		setDeleteOptionTarget({ uid: opt.optionUid, name: opt.translations["en-us"] || "this option" });
		setShowDeleteOptionConfirm(true);
	};

	const confirmDeleteOption = () => {
		if (!deleteOptionTarget) return;
		deleteDimensionOption.mutate(
			{ categoryDimensionOptionUid: deleteOptionTarget.uid },
			{ onSuccess: () => refetch() },
		);
		setShowDeleteOptionConfirm(false);
		setDeleteOptionTarget(null);
	};

	if (isLoading) {
		return <Loader message="Loading options..." />;
	}

	return (
		<>
			<div className="d-flex flex-column gap-2">
				{grouped.length > 0 ? (
					grouped.map((opt) => (
						<div key={opt.optionId} className="rounded border bg-white px-3 py-2">
							<div className="d-flex align-items-center">
								{LANGUAGES.map((lang) => (
									<small key={lang.key} className="text-muted" style={{ minWidth: 180, flex: "1 1 0" }}>
										<span className="fw-semibold">{lang.label}:</span>{" "}
										{opt.translations[lang.key] || "—"}
									</small>
								))}
								{opt.isTextbox && (
									<Badge bg="info" pill className="me-2">Textbox</Badge>
								)}
								<small className="text-muted text-nowrap me-2">
									Seq: {opt.sequence}
								</small>
								<Button
									variant="outline-primary"
									size="sm"
									className="py-0 px-1 me-1"
									onClick={() => handleEditOption(opt)}
								>
									<i className="bi bi-pencil-square"></i>
								</Button>
								<Button
									variant="outline-danger"
									size="sm"
									className="py-0 px-1"
									onClick={() => handleDeleteOptionClick(opt)}
								>
									<i className="bi bi-trash"></i>
								</Button>
							</div>
						</div>
					))
				) : (
					<p className="text-muted mb-0">No options configured.</p>
				)}
				<div>
					<Button
						variant="outline-primary"
						size="sm"
						className="d-flex align-items-center gap-1"
						onClick={handleAddOption}
					>
						<i className="bi bi-plus-lg"></i>
						Add Option
					</Button>
				</div>
			</div>

			<DimensionOptionFormModal
				show={showOptionModal}
				onHide={() => setShowOptionModal(false)}
				onSubmit={handleOptionSubmit}
				title={optionModalTitle}
				defaultValues={optionModalDefaults}
			/>

			<Modal show={showDeleteOptionConfirm} onHide={() => setShowDeleteOptionConfirm(false)} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Confirm Delete</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Delete option &quot;{deleteOptionTarget?.name}&quot;?
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" size="sm" onClick={() => setShowDeleteOptionConfirm(false)}>
						Cancel
					</Button>
					<Button variant="danger" size="sm" onClick={confirmDeleteOption}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}

export default function QuestionsAnswers({ editCategories }: { editCategories: EditSubCategory[] }) {
	const { userData } = useAuth();
	const portalId = userData?.user.portals.find((f) => f !== 0) ?? 0;

	const { data: dimensions, isLoading, refetch } = useGetCategoryDimensions({
		portalId
	});

	const createCategoryDimension = useCreateCategoryDimension();
	const deleteCategoryDimension = useDeleteCategoryDimension();
	const updateCategoryDimension = useUpdateCategoryDimension();

	const [searchText, setSearchText] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedSubcategory, setSelectedSubcategory] = useState("");
	const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
	const [showModal, setShowModal] = useState(false);
	const [modalTitle, setModalTitle] = useState("New Question");
	const [editingUid, setEditingUid] = useState<string | null>(null);
	const [modalDefaults, setModalDefaults] = useState<Partial<QuestionFormData> | undefined>(undefined);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{ uid: string; name: string } | null>(null);

	const categories = useMemo(() => {
		if (!dimensions) return [];
		const unique = [...new Set(dimensions.map((d) => d.CategoryLocalization).filter(Boolean))];
		return unique.sort();
	}, [dimensions]);

	const subcategories = useMemo(() => {
		if (!dimensions) return [];
		const filtered = selectedCategory
			? dimensions.filter((d) => d.CategoryLocalization === selectedCategory)
			: dimensions;
		const unique = [...new Set(filtered.map((d) => d.ChildCategoryLocalization).filter(Boolean))];
		return unique.sort();
	}, [dimensions, selectedCategory]);

	const filtered = useMemo(() => {
		if (!dimensions) return [];
		return dimensions.filter((dim) => {
			if (selectedCategory && dim.CategoryLocalization !== selectedCategory) return false;
			if (selectedSubcategory && dim.ChildCategoryLocalization !== selectedSubcategory) return false;
			if (searchText) {
				const term = searchText.toLowerCase();
				if (
					!dim["en-us"]?.toLowerCase().includes(term) &&
					!dim.CategoryLocalization?.toLowerCase().includes(term) &&
					!dim.ChildCategoryLocalization?.toLowerCase().includes(term)
				) {
					return false;
				}
			}
			return true;
		});
	}, [dimensions, searchText, selectedCategory, selectedSubcategory]);

	const handleAccordionChange = (eventKeys: string | string[]) => {
		const keys = Array.isArray(eventKeys) ? eventKeys : [eventKeys];
		// Prevent expanding textbox questions
		const allowedKeys = keys.filter((key) => {
			const dim = dimensions?.find((d) => String(d.CategoryDimension_ID) === key);
			return dim && !dim.IsTextbox;
		});
		setExpandedKeys(allowedKeys);
	};

	const handleQuestionSubmit = (data: QuestionFormData) => {
		const localizations = {
			"en-us": data["en-us"],
			"de-ch": data["de-ch"],
			"de-de": data["de-de"],
			"fr-fr": data["fr-fr"],
			"it-it": data["it-it"],
		};

		if (editingUid) {
			updateCategoryDimension.mutate(
				{
					categoryDimensionUid: editingUid,
					sequence: data.sequence,
					weight: data.weight,
					isTextbox: data.isTextbox,
					isBackofficeRequired: data.isBackofficeRequired,
					isPublicRequired: data.isPublicRequired,
					localizations,
				},
				{ onSuccess: () => refetch() },
			);
		} else {
			createCategoryDimension.mutate(
				{
					itemCategoryId: data.itemCategoryId,
					weight: data.weight,
					isTextbox: data.isTextbox,
					sequence: data.sequence,
					isPublicRequired: data.isPublicRequired,
					isBackofficeRequired: data.isBackofficeRequired,
					localizations,
				},
				{ onSuccess: () => refetch() },
			);
		}
	};

	const handleAddClick = () => {
		setEditingUid(null);
		setModalTitle("New Question");
		setModalDefaults(undefined);
		setShowModal(true);
	};

	const handleEditClick = (dim: (typeof filtered)[number]) => {
		// Find the parent category ID from editCategories
		const subCat = editCategories.find((c) => c.ItemCategory_ID === dim.ItemCategory_ID);
		const parentCategoryId = subCat?.ItemCategory_fk || "";

		setEditingUid(dim.CategoryDimension_UID);
		setModalTitle(`Edit: ${dim["en-us"]}`);
		setModalDefaults({
			categoryId: parentCategoryId,
			itemCategoryId: dim.ItemCategory_ID,
			"en-us": dim["en-us"] || "",
			"de-ch": dim["de-ch"] || "",
			"de-de": dim["de-de"] || "",
			"fr-fr": dim["fr-fr"] || "",
			"it-it": dim["it-it"] || "",
			weight: dim.Weight,
			isTextbox: !!dim.IsTextbox,
			sequence: dim.Sequence,
			isPublicRequired: !!dim.IsPublicRequired,
			isBackofficeRequired: !!dim.IsBackofficeRequired,
		});
		setShowModal(true);
	};

	const handleDeleteClick = (uid: string, name: string) => {
		setDeleteTarget({ uid, name });
		setShowDeleteConfirm(true);
	};

	const confirmDelete = () => {
		if (!deleteTarget) return;
		deleteCategoryDimension.mutate(
			{ categoryDimensionUID: deleteTarget.uid },
			{ onSuccess: () => refetch() },
		);
		setShowDeleteConfirm(false);
		setDeleteTarget(null);
	};

	if (isLoading) {
		return <Loader message="Loading questions..." />;
	}

	return (
		<div className="card border-0 shadow-sm">
			<div className="card-body p-0">
				<div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
					<div className="d-flex align-items-center gap-2">
						<small className="text-muted">
							{filtered.length} of {dimensions?.length ?? 0} questions
						</small>
						<Button
							variant="primary"
							size="sm"
							className="d-flex align-items-center gap-1"
							onClick={handleAddClick}
						>
							<i className="bi bi-plus-lg"></i>
							New Question
						</Button>
					</div>
					<div className="d-flex align-items-center gap-2">
						<Form.Select
							size="sm"
							style={{ maxWidth: 180 }}
							value={selectedCategory}
							onChange={(e) => {
								setSelectedCategory(e.target.value);
								setSelectedSubcategory("");
							}}
						>
							<option value="">All Categories</option>
							{categories.map((cat) => (
								<option key={cat} value={cat}>{cat}</option>
							))}
						</Form.Select>
						<Form.Select
							size="sm"
							style={{ maxWidth: 180 }}
							value={selectedSubcategory}
							onChange={(e) => setSelectedSubcategory(e.target.value)}
						>
							<option value="">All Subcategories</option>
							{subcategories.map((sub) => (
								<option key={sub} value={sub}>{sub}</option>
							))}
						</Form.Select>
						<div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
							<span className="input-group-text bg-white border-end-0">
								<i className="bi bi-search text-muted"></i>
							</span>
							<input
								type="text"
								className="form-control border-start-0"
								placeholder="Search..."
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
							/>
						</div>
					</div>
				</div>

				{filtered.length > 0 ? (
					<Accordion alwaysOpen flush activeKey={expandedKeys} onSelect={(keys) => handleAccordionChange(keys as string | string[])}>
						{filtered.map((dim) => {
							const eventKey = String(dim.CategoryDimension_ID);
							const isExpanded = expandedKeys.includes(eventKey);

							return (
								<Accordion.Item eventKey={eventKey} key={dim.CategoryDimension_ID} className={dim.IsTextbox ? "accordion-no-toggle" : ""}>
									<Accordion.Header className={dim.IsTextbox ? "pe-none" : ""}>
										<div className="d-flex align-items-center justify-content-between w-100 me-3">
											<div className="d-flex align-items-center gap-3">
												<div className="d-flex flex-column" style={{ minWidth: 140 }}>
													<span className="fw-semibold small">{dim.ChildCategoryLocalization}</span>
													<small className="text-muted">{dim.CategoryLocalization}</small>
												</div>
												<span className="fw-semibold">{dim["en-us"]}</span>
												{!!dim.IsTextbox && (
													<Badge bg="info" pill>Textbox</Badge>
												)}
											</div>
											<div className="d-flex align-items-center gap-3">
												<small className="text-muted">Seq: {dim.Sequence}</small>
												<div className="d-flex align-items-center gap-1 pe-auto">
													<i
														className={`bi bi-globe ${dim.IsPublicRequired ? "text-success" : "text-secondary opacity-50"}`}
														title={dim.IsPublicRequired ? "Public required" : "Public not required"}
													></i>
													<i
														className={`bi bi-building ${dim.IsBackofficeRequired ? "text-success" : "text-secondary opacity-50"}`}
														title={dim.IsBackofficeRequired ? "Backoffice required" : "Backoffice not required"}
													></i>
												</div>
												<Button
													variant="outline-primary"
													size="sm"
													className="d-flex align-items-center gap-1 py-0 px-1 pe-auto"
													onClick={(e) => {
														e.stopPropagation();
														handleEditClick(dim);
													}}
												>
													<i className="bi bi-pencil-square"></i>
												</Button>
												<Button
													variant="outline-danger"
													size="sm"
													className="d-flex align-items-center gap-1 py-0 px-1 pe-auto"
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteClick(dim.CategoryDimension_UID, dim["en-us"]);
													}}
												>
													<i className="bi bi-trash"></i>
												</Button>
											</div>
										</div>
									</Accordion.Header>
									{!dim.IsTextbox && (
										<Accordion.Body className="bg-light bg-opacity-50">
											<h6 className="mb-2">Options</h6>
											<DimensionOptions
												categoryDimensionUid={dim.CategoryDimension_UID}
												portalId={portalId}
												isExpanded={isExpanded}
											/>
										</Accordion.Body>
									)}
								</Accordion.Item>
							);
						})}
					</Accordion>
				) : (
					<div className="text-center text-muted py-4">
						<i className="bi bi-inbox me-1"></i>
						No questions found.
					</div>
				)}
			</div>

			<QuestionFormModal
				show={showModal}
				onHide={() => setShowModal(false)}
				onSubmit={handleQuestionSubmit}
				title={modalTitle}
				defaultValues={modalDefaults}
				editCategories={editCategories}
				isEditMode={!!editingUid}
			/>

			<Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Confirm Delete</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Delete question &quot;{deleteTarget?.name}&quot;?
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
						Cancel
					</Button>
					<Button variant="danger" size="sm" onClick={confirmDelete}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
